import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import PageHero from '@/components/PageHero';
import { getArtists } from '@/lib/api';
import PictureImg from '@/components/PictureImg';

const roman = (i) => ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'][i] || String(i + 1);

export default function Artists() {
    useDocumentMeta({
        title: 'Artists · Maison Touré',
        description: 'A small, deliberately curated roster of contemporary African artists represented by Maison Touré — painting, sculpture, photography, textile.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const hero = settings?.artists_page || {};
    const [artists, setArtists] = useState([]);
    useEffect(() => { getArtists().then(setArtists); }, []);

    return (
        <div ref={reveal} data-testid="artists-page" style={{ paddingTop: 144 }}>
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12 pb-40 md:pb-56">
                <PageHero
                    testid="artists-hero"
                    overline={hero.hero_overline ?? 'The Programme'}
                    title={hero.hero_title ?? 'Artists.'}
                    intro={hero.hero_intro ?? 'Maison Touré represents a small, deliberately curated roster of contemporary African artists working across painting, sculpture, photography and textile — each engaged across years, not seasons.'}
                />

                <div className="mt-32 md:mt-40 flex flex-col gap-32 md:gap-48">
                    {artists.map((a, i) => (
                        <Link
                            to={`/artists/${a.slug}`}
                            key={a.slug}
                            className="reveal grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center group"
                            data-testid={`artist-card-${a.slug}`}
                        >
                            <div className="md:col-span-7">
                                <div className="img-wrap aspect-[5/4]" style={{ background: 'var(--bg-3)' }}>
                                    <PictureImg src={a.portrait} alt={a.name} loading="lazy" />
                                </div>
                            </div>
                            <div className="md:col-span-4 md:col-start-9">
                                <span className="overline" style={{ color: 'var(--ink-3)' }}>{roman(i)}</span>
                                <h2
                                    className="mt-6"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 'clamp(40px, 4.6vw, 72px)',
                                        lineHeight: 1.04,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    {a.name}
                                </h2>
                                <div
                                    className="mt-3"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontSize: 18,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {a.origin} &nbsp;·&nbsp; {a.born}
                                </div>
                                <p
                                    className="mt-8"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontWeight: 400,
                                        fontSize: 17,
                                        lineHeight: 1.7,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    {a.statement}
                                </p>
                                <span className="btn-ghost mt-10 inline-flex">View artist</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
